/**
 * iter87.361 — NWSL Match Predictor: 3 independent sub-picks per
 * playoff game (Winner 3pts / Decided-by Regulation-ExtraTime-
 * Penalties 2pts / Total Goals over-under a fixed 2.5 line 5pts),
 * each locking at THAT game's own kickoff. Fixed scoring, not
 * admin-editable (unlike Champions/Dark Horse) — this modal is
 * read/write for picks only.
 *
 * iter87.467 (user-requested) — rebuilt onto the same MLB Series
 * Predictor 3-section shape every other multi-round predictor now
 * uses: an always-visible "Open — Make Your Pick" section, a
 * "Locked & Live" section (collapsible, starts open), and a
 * "Completed" section (collapsible, starts closed, sub-picks tinted
 * green/red for hit/miss) — every round stays visible, nothing is
 * ever hidden behind a round switcher.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronRight, ChevronDown, Target, Check, Lock, Info, Trophy } from "lucide-react";
import { BentoModal } from "@/components/onui/BentoMotion";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { SubPickLabel } from "@/components/dashboard/SubPickLabel";
import { TeamSwatch } from "@/components/dashboard/TeamSwatch";

const TIME_BUCKETS = [
    { key: "regulation", label: "Regulation" },
    { key: "extra_time", label: "Extra Time" },
    { key: "penalties", label: "Penalties" },
];
const GOALS_LINE = 2.5;

export default function NwslPredictorModal({ open, onClose, onSaved, tournamentId, participantId, existingPicks }) {
    const [draft, setDraft] = useState({});
    const [bracket, setBracket] = useState(null);
    const [saving, setSaving] = useState(false);
    const [showLive, setShowLive] = useState(true);
    const [showCompleted, setShowCompleted] = useState(false);

    useEffect(() => {
        if (!open || !tournamentId) return;
        setDraft(existingPicks || {});
        api.getNwslBracket(tournamentId).then(setBracket).catch(() => setBracket(null));
    }, [open, tournamentId, existingPicks]);

    const teamsById = useMemo(() => {
        const out = {};
        (bracket?.teams || []).forEach((t) => { out[t.id] = t; });
        return out;
    }, [bracket]);

    if (!open) return null;
    // testing-agent fix: /nwsl/.../bracket fixtures carry NO `status` field
    // (pre/live/post only exists on scheduled_matches rows) — derive it here
    // from played/scheduled_at (same rule as the old `fixtureLocked`).
    const fixtures = (bracket?.fixtures || []).map((f) => ({
        ...f,
        status: f.status || (f.played ? "post"
            : (f.scheduled_at && new Date(f.scheduled_at).getTime() <= Date.now()) ? "live" : "pre"),
    }));
    const roundLabels = bracket?.round_labels || { qf: "Quarter-finals", sf: "Semi-finals", final: "Final" };
    const withLabel = (fx) => ({ ...fx, _roundLabel: roundLabels[fx.round] || fx.round });
    const openFixtures = fixtures.filter((f) => f.status === "pre").map(withLabel);
    const liveFixtures = fixtures.filter((f) => f.status === "live").map(withLabel);
    const completedFixtures = fixtures.filter((f) => f.status === "post").map(withLabel);
    const hasAnyFixtures = fixtures.length > 0;

    const byRound = (list) => list.reduce((acc, f) => {
        if (!acc[f.round]) acc[f.round] = { label: f._roundLabel, fixtures: [] };
        acc[f.round].fixtures.push(f);
        return acc;
    }, {});
    const liveByRound = byRound(liveFixtures);
    const completedByRound = byRound(completedFixtures);

    const setSub = (fid, field, value) => {
        setDraft((d) => ({ ...d, [fid]: { ...(d[fid] || {}), [field]: value } }));
    };

    const dirtyIds = Object.keys(draft).filter((fid) => {
        const d = draft[fid] || {};
        const e = (existingPicks || {})[fid] || {};
        return d.winner_team_id !== e.winner_team_id || d.time_bucket !== e.time_bucket || d.goals_side !== e.goals_side;
    });

    const saveAll = async () => {
        setSaving(true);
        try {
            await Promise.all(dirtyIds.map((fid) => api.submitNwslPredictorPick(tournamentId, participantId, fid, draft[fid])));
            onSaved?.(draft);
            toast.success("Playoff Predictor picks saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save your picks");
        } finally {
            setSaving(false);
        }
    };

    const renderFixture = (fx) => {
        const teamA = teamsById[fx.team_a_id];
        const teamB = teamsById[fx.team_b_id];
        const locked = fx.status !== "pre";
        const isFinal = fx.status === "post";
        const d = draft[fx.id] || {};
        const actualOuSide = isFinal && fx.total_goals != null ? (fx.total_goals > GOALS_LINE ? "over" : "under") : null;
        const cellClass = (picked, correctNow, base) => {
            if (!picked) return `border-white/[0.08] bg-[#1a1a1a] text-zinc-300 ${locked ? "opacity-50" : ""}`;
            if (isFinal) return correctNow ? "border-emerald-500 bg-emerald-500/10 text-emerald-400" : "border-red-500 bg-red-500/10 text-red-400";
            return `${base} ${locked ? "opacity-50" : ""}`;
        };
        return (
            <div
                key={fx.id}
                data-testid={`nwsl-predictor-fixture-${fx.id}`}
                className={`rounded-lg border p-3 ${locked ? "border-white/[0.06] bg-white/[0.015] opacity-80" : "border-white/[0.08] bg-[#141414]"}`}
            >
                <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                    <span className="inline-flex items-center rounded-lg bg-neutral-950 border border-white/10 text-[9px] font-bold px-2 py-0.5 uppercase tracking-wider text-zinc-500">
                        {fx._roundLabel}
                    </span>
                    {/* iter87.566 (user-requested — "not quite right...
                        just wanted the swatch v swatch in the top right
                        corner... the actual [winner] tile can have
                        both [swatch + name]") — header is swatch-only
                        so the team name isn't shown twice; full name +
                        swatch lives on the Winner buttons below. */}
                    <div className="flex items-center gap-1.5 shrink-0" data-testid={`nwsl-predictor-matchup-${fx.id}`}>
                        <TeamSwatch name={teamA?.name} sport="nwsl" size="w-5 h-5 text-[8px]" />
                        <span className="text-zinc-600 text-xs">vs</span>
                        <TeamSwatch name={teamB?.name} sport="nwsl" size="w-5 h-5 text-[8px]" />
                    </div>
                    {fx.played ? (
                        <span className="text-[10px] font-mono text-zinc-400 shrink-0">
                            FT {fx.score_a}-{fx.score_b}{fx.resolution === "penalties" ? " (PK)" : fx.resolution === "extra_time" ? " (ET)" : ""}
                        </span>
                    ) : locked ? (
                        <span className="text-[9px] uppercase tracking-widest text-heat font-bold shrink-0 flex items-center gap-1"><Lock className="w-2.5 h-2.5" /> Locked</span>
                    ) : null}
                </div>

                {/* iter87.562 (user-requested — "make those labeled a
                    little better") — a tiny uppercase group label above
                    each of the 3 independent sub-picks, so a player
                    doesn't have to re-read the "How it works" blurb to
                    know what a given row of buttons means. */}
                <SubPickLabel>
                    Winner <span className="text-zinc-400 normal-case tracking-normal font-mono">+3</span>
                </SubPickLabel>
                <div className="grid grid-cols-2 gap-1.5 mb-2">
                    {[teamA, teamB].filter(Boolean).map((t) => {
                        const picked = d.winner_team_id === t.id;
                        const correct = isFinal && fx.winner_team_id === t.id;
                        return (
                            <button
                                key={t.id}
                                type="button"
                                disabled={locked}
                                data-testid={`nwsl-predictor-winner-${fx.id}-${t.id}`}
                                onClick={() => setSub(fx.id, "winner_team_id", t.id)}
                                className={`px-2 py-1.5 rounded-lg border text-[11px] font-bold disabled:cursor-not-allowed flex items-center justify-center gap-1.5 ${cellClass(picked, correct, "border-cyber bg-cyber/15 text-cyber")}`}
                            >
                                <TeamSwatch name={t.name} sport="nwsl" size="w-5 h-5 text-[8px]" />
                                <span className="truncate min-w-0">{t.name}</span>
                                {picked && <Check className="w-3 h-3 shrink-0" />}
                            </button>
                        );
                    })}
                </div>

                <SubPickLabel>
                    Game Ends In <span className="text-zinc-400 normal-case tracking-normal font-mono">+2</span>
                </SubPickLabel>
                <div className="grid grid-cols-3 gap-1.5 mb-2">
                    {TIME_BUCKETS.map((tb) => {
                        const picked = d.time_bucket === tb.key;
                        const correct = isFinal && fx.resolution === tb.key;
                        return (
                            <button
                                key={tb.key}
                                type="button"
                                disabled={locked}
                                data-testid={`nwsl-predictor-time-${fx.id}-${tb.key}`}
                                onClick={() => setSub(fx.id, "time_bucket", tb.key)}
                                className={`px-1.5 py-1.5 rounded-lg border text-[10px] font-bold disabled:cursor-not-allowed ${cellClass(picked, correct, "border-volt bg-volt/15 text-volt")}`}
                            >
                                {tb.label}
                            </button>
                        );
                    })}
                </div>

                <SubPickLabel>
                    Total Goals <span className="text-zinc-400 normal-case tracking-normal font-mono">+5</span>
                </SubPickLabel>
                <div className="grid grid-cols-2 gap-1.5">
                    {["over", "under"].map((side) => {
                        const picked = d.goals_side === side;
                        const correct = isFinal && actualOuSide === side;
                        return (
                            <button
                                key={side}
                                type="button"
                                disabled={locked}
                                data-testid={`nwsl-predictor-goals-${fx.id}-${side}`}
                                onClick={() => setSub(fx.id, "goals_side", side)}
                                className={`px-2 py-1.5 rounded-lg border text-[11px] font-bold capitalize disabled:cursor-not-allowed ${cellClass(picked, correct, "border-neon bg-neon/15 text-neon")}`}
                            >
                                {side} {GOALS_LINE}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <BentoModal
            open={open}
            onClose={onClose}
            testId="nwsl-predictor-modal"
            backdropClassName="lg:items-center lg:justify-center lg:p-4"
            panelClassName="rounded-2xl border border-cyber/40 bg-obsidian max-w-2xl w-full max-h-[90dvh] overflow-hidden flex flex-col"
        >
            <div className="flex items-center justify-between gap-2 px-4 sm:px-6 pt-4 sm:pt-6 pb-3 border-b border-white/[0.06] shrink-0">
                <div className="min-w-0 flex items-center gap-2 flex-1">
                    <div className="w-9 h-9 rounded-xl bg-cyber flex items-center justify-center shrink-0">
                        <Target className="w-4 h-4 text-black" strokeWidth={2.5} />
                    </div>
                    <h3 className="font-display font-extrabold text-lg uppercase truncate">Playoff Predictor</h3>
                </div>
                <button
                    onClick={onClose}
                    className="w-10 h-10 rounded-full bg-white/[0.06] hover:bg-white/[0.12] flex items-center justify-center transition-colors active:scale-95 shrink-0"
                    data-testid="close-nwsl-predictor-modal-btn"
                    aria-label="Close"
                >
                    <ChevronRight className="w-4 h-4 rotate-90" />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">

            <div className="text-[11px] text-zinc-500 mb-4 flex gap-2 items-start text-left" data-testid="nwsl-predictor-explainer">
                <Info className="w-4 h-4 text-cyber shrink-0 mt-0.5" />
                <p className="leading-relaxed">
                    <span className="text-white font-bold uppercase tracking-tight">How it works:</span>{" "}
                    Per game: pick the <span className="text-cyber font-bold">Winner (+3 pts)</span>, how it's decided — Regulation / Extra Time / Penalties <span className="text-zinc-400 font-bold">(+2 pts)</span>, and whether total goals go Over or Under {GOALS_LINE} <span className="text-volt font-bold">(+5 pts)</span> <span className="text-zinc-500 italic">(shootout goals don't count)</span>. Each game locks at its own kickoff — every round stays visible below.
                </p>
            </div>

            {!hasAnyFixtures && (
                <p data-testid="nwsl-predictor-not-set-notice" className="text-xs text-zinc-500 -mt-2 mb-3">
                    No games are set yet — check back once the bracket is drawn. Sections below will fill in as matchups are scheduled.
                </p>
            )}
            <div className="space-y-4">
                <div className="space-y-2.5">
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold" data-testid="nwsl-predictor-open-heading">
                        Open — Make Your Pick ({openFixtures.length})
                    </p>
                    {openFixtures.length > 0 ? openFixtures.map(renderFixture) : (
                        <p className="text-xs text-zinc-500" data-testid="nwsl-predictor-no-open-notice">
                            No open games right now — check back once new matchups are set.
                        </p>
                    )}
                </div>

                <Collapsible open={showLive} onOpenChange={setShowLive}>
                        <CollapsibleTrigger
                            data-testid="nwsl-predictor-live-toggle"
                            className="flex items-center justify-between w-full py-2 border-t border-white/[0.08] text-left"
                        >
                            <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold flex items-center gap-1.5">
                                <Lock className="w-3 h-3" /> Locked & Live ({liveFixtures.length})
                            </span>
                            <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${showLive ? "rotate-180" : ""}`} />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="space-y-4 pt-2.5" data-testid="nwsl-predictor-live-content">
                            {liveFixtures.length > 0 ? (
                                Object.entries(liveByRound).map(([roundKey, group]) => (
                                    <div key={roundKey} className="space-y-2.5">
                                        <p className="text-[9px] uppercase tracking-widest text-zinc-600 font-bold">{group.label}</p>
                                        {group.fixtures.map(renderFixture)}
                                    </div>
                                ))
                            ) : (
                                <p className="text-xs text-zinc-500" data-testid="nwsl-predictor-live-empty-notice">
                                    Nothing locked or in progress right now.
                                </p>
                            )}
                        </CollapsibleContent>
                    </Collapsible>

                    <Collapsible open={showCompleted} onOpenChange={setShowCompleted}>
                        <CollapsibleTrigger
                            data-testid="nwsl-predictor-completed-toggle"
                            className="flex items-center justify-between w-full py-2 border-t border-white/[0.08] text-left"
                        >
                            <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold flex items-center gap-1.5">
                                <Trophy className="w-3 h-3" /> Completed ({completedFixtures.length})
                            </span>
                            <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${showCompleted ? "rotate-180" : ""}`} />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="space-y-4 pt-2.5" data-testid="nwsl-predictor-completed-content">
                            {completedFixtures.length > 0 ? (
                                Object.entries(completedByRound).map(([roundKey, group]) => (
                                    <div key={roundKey} className="space-y-2.5">
                                        <p className="text-[9px] uppercase tracking-widest text-zinc-600 font-bold">{group.label}</p>
                                        {group.fixtures.map(renderFixture)}
                                    </div>
                                ))
                            ) : (
                                <p className="text-xs text-zinc-500" data-testid="nwsl-predictor-completed-empty-notice">
                                    No games have finished yet.
                                </p>
                            )}
                        </CollapsibleContent>
                    </Collapsible>
                </div>

            </div>

            <div className="shrink-0 glass border-t border-white/10 px-4 sm:px-6 py-3" data-testid="nwsl-predictor-footer">
                <div className="flex items-center justify-between gap-3">
                    <span className="text-[11px] uppercase tracking-widest font-bold text-zinc-400" data-testid="nwsl-predictor-dirty-count">
                        {dirtyIds.length > 0 ? `${dirtyIds.length} game${dirtyIds.length === 1 ? "" : "s"} changed` : "No changes to save"}
                    </span>
                    <button
                        type="button"
                        disabled={saving || dirtyIds.length === 0}
                        onClick={saveAll}
                        data-testid="nwsl-predictor-save-btn"
                        className="shrink-0 inline-flex items-center justify-center gap-1.5 rounded-full border-2 border-black bg-cyber text-black font-display font-extrabold uppercase tracking-wide text-xs px-4 min-h-[44px] transition-all duration-150 active:scale-95 disabled:opacity-40 disabled:pointer-events-none"
                    >
                        <Check className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save Picks"}
                    </button>
                </div>
            </div>
        </BentoModal>
    );
}
